import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReturnButton from '../../components/ui/ReturnButton';
import CardItem from './sections/CardItem';
import Header from '../../components/layout/Header/Header';
import Footer from '../../components/layout/Footer/Footer';

const LIKED_ITEMS_KEY = 'liked_items';

const TagItems = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [tag, setTag] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [likedItems, setLikedItems] = useState({});
  const [isLikesLoaded, setIsLikesLoaded] = useState(false);
  const [rawItems, setRawItems] = useState([]);
  
  const scrollPositionRef = useRef(0);

  // 1. ЗАГРУЗКА ЛАЙКОВ ИЗ LOCALSTORAGE
  useEffect(() => {
    const savedLikes = localStorage.getItem(LIKED_ITEMS_KEY);
    console.log('Читаем localStorage, savedLikes:', savedLikes);
    
    if (savedLikes) {
      try {
        const parsedLikes = JSON.parse(savedLikes);
        setLikedItems(parsedLikes);
        console.log('Загружены лайки из localStorage:', parsedLikes);
      } catch (e) {
        console.error('Ошибка при загрузке лайков:', e);
      }
    }
    setIsLikesLoaded(true);
  }, []);

  // 2. СОХРАНЕНИЕ ЛАЙКОВ
  useEffect(() => {
    if (isLikesLoaded) {
      localStorage.setItem(LIKED_ITEMS_KEY, JSON.stringify(likedItems));
      console.log('Сохранены лайки в localStorage:', likedItems);
    }
  }, [likedItems, isLikesLoaded]);

  // 3. ЗАГРУЗКА ДАННЫХ С СЕРВЕРА
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const apiUrl = process.env.REACT_APP_API_URL;

        const [tagResponse, routesResponse, locationsResponse] = await Promise.all([
          fetch(`${apiUrl}/tags/${id}`),
          fetch(`${apiUrl}/tags/${id}/routes`),
          fetch(`${apiUrl}/tags/${id}/locations`)
        ]);

        if (!tagResponse.ok) {
          throw new Error('Тег не найден');
        }

        const tagData = await tagResponse.json();
        const normalizedTag = tagData.data || tagData;
        
        let routesData = [];
        if (routesResponse.ok) {
          const routesResult = await routesResponse.json();
          routesData = routesResult.data || (Array.isArray(routesResult) ? routesResult : []);
        }
        
        let locationsData = [];
        if (locationsResponse.ok) {
          const locationsResult = await locationsResponse.json();
          locationsData = locationsResult.data || (Array.isArray(locationsResult) ? locationsResult : []);
        }
        
        const mergedItems = [...routesData, ...locationsData];
        
        console.log('Загруженный тег:', normalizedTag);
        console.log('Объединённые элементы:', mergedItems);

        setTag(normalizedTag);
        setRawItems(mergedItems);

      } catch (err) {
        console.error('Ошибка:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      scrollPositionRef.current = window.scrollY;
      fetchData();
    }
  }, [id]);

  // 4. ПРИМЕНЯЕМ СОРТИРОВКУ
  useEffect(() => {
    if (isLikesLoaded && rawItems.length > 0) {
      console.log('Применяем сортировку с лайками:', likedItems);
      
      const sortedItems = [...rawItems].sort((a, b) => {
        const aIsLiked = likedItems[a.id] === true;
        const bIsLiked = likedItems[b.id] === true;
        
        if (aIsLiked && !bIsLiked) return -1;
        if (!aIsLiked && bIsLiked) return 1;
        return 0;
      });
      
      setItems(sortedItems);
      
      if (scrollPositionRef.current > 0) {
        setTimeout(() => {
          window.scrollTo(0, scrollPositionRef.current);
          scrollPositionRef.current = 0;
        }, 0);
      }
    }
  }, [isLikesLoaded, rawItems, likedItems]);

  // 5. ОБНОВЛЕНИЕ ПРИ ИЗМЕНЕНИИ ЛАЙКОВ
  useEffect(() => {
    if (isLikesLoaded && rawItems.length > 0 && items.length > 0) {
      const currentScroll = window.scrollY;
      
      const sortedItems = [...rawItems].sort((a, b) => {
        const aIsLiked = likedItems[a.id] === true;
        const bIsLiked = likedItems[b.id] === true;
        
        if (aIsLiked && !bIsLiked) return -1;
        if (!aIsLiked && bIsLiked) return 1;
        return 0;
      });
      
      setItems(sortedItems);
      
      setTimeout(() => {
        window.scrollTo(0, currentScroll);
      }, 0);
    }
  }, [likedItems, rawItems, isLikesLoaded, items.length]);

  const handleReturn = () => {
    navigate(-1);
  };

  const getItemLink = (item) => {
    if (item.latitude !== undefined || item.longitude !== undefined) {
      return `/locations/${item.id}`;
    }
    if (item.distance !== undefined || item.steps !== undefined) {
      return `/routes/${item.id}`;
    }
    return `/locations/${item.id}`;
  };

  const handleToggleLike = useCallback((itemId, isLiked) => {
    setLikedItems(prev => {
      const newLikedItems = { ...prev, [itemId]: isLiked };
      if (!isLiked) {
        delete newLikedItems[itemId];
      }
      console.log('Обновляем лайки, новое состояние:', newLikedItems);
      return newLikedItems;
    });
  }, []);

  if (loading || !isLikesLoaded) {
    return (
      <div id="tagChoicePageContainer" className="flex-column">
        <Header />
        <div className="loading-spinner">Загрузка...</div>
        <Footer />
      </div>
    );
  }

  if (error || !tag) {
    return (
      <div id="tagChoicePageContainer" className="flex-column">
        <Header />
        <div className="error-message">Ошибка: {error || 'Тег не найден'}</div>
        <ReturnButton onClick={handleReturn} text="Назад" />
        <Footer />
      </div>
    );
  }

  return (
    <div id="tagChoicePageContainer" className="flex-column">
      <Header />
      <ReturnButton 
        onClick={handleReturn}
        text="Назад"
        className="category-return-btn"
      />
      
      <div className="category-header">
        <h1 className="tagChoicePageName">
          {tag.name}
        </h1>
        {tag.description && (
          <div className="tag-description">
            {tag.description.split('\\n').map((line, index) => (
              <p key={index}>{line}</p>
            ))}
          </div>
        )}
      </div>
      
      <div id="cardItemWrapper">
        {items.length === 0 ? (
          <div className="no-items-message">Нет элементов в этом теге</div>
        ) : (
          items.map((item) => (
            <CardItem 
              key={item.id}
              id={item.id}
              name={item.name}
              isLiked={likedItems[item.id] === true}
              image={item.main_photo_url}
              textBg='/elements/tagCultureLocs/c2.svg'
              link={getItemLink(item)}
              onToggleLike={handleToggleLike}
            />
          ))
        )}
      </div>
      
      <Footer />
    </div>
  );
};

export default TagItems;